import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  ChevronRight,
  Sparkles,
  Users,
  Package,
  Wrench,
  CalendarClock,
  ShoppingCart,
  ArrowUpRight,
} from "lucide-react";
import api from "../../lib/api";

const KIND_ICON = {
  customer: Users,
  inventory: Package,
  jobcard: Wrench,
  equipment: CalendarClock,
  order_request: ShoppingCart,
};

const KIND_LINK = (kind, item) => {
  switch (kind) {
    case "customer":
      return `/customers/${item.id}`;
    case "inventory":
      return `/equipment`;
    case "jobcard":
      return `/jobcards/${item.id}`;
    case "equipment":
      return item.customer_id ? `/customers/${item.customer_id}` : `/scheduler`;
    case "order_request":
      return `/order-requests`;
    default:
      return "/";
  }
};

/**
 * Admin "Unresolved" tile — surfaces every record across the app that
 * needs a quick fix (missing contact info, jobs without invoices,
 * inventory without intervals, pending order requests…).  Drives the
 * "let's get to 100%" gamification on the dashboard.
 *
 * Backend: GET /api/dashboard/unresolved (admin/manager/owner)
 */
export default function UnresolvedWidget() {
  const [data, setData] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    setErr("");
    api
      .get("/dashboard/unresolved")
      .then((r) => setData(r.data))
      .catch((e) => {
        // 403 for non-admins is expected — silently hide.
        if (e?.response?.status === 403) {
          setData({ blocked: true });
        } else {
          setErr("Could not load unresolved counter.");
        }
      });
  };

  useEffect(() => {
    load();
  }, []);

  if (!data || data.blocked) return null;

  const { sections = [], total = 0, completeness_pct = 100 } = data;

  return (
    <section
      className={`hv-card p-4 flex flex-col overflow-hidden ${total === 0 ? "md:h-[320px]" : "max-h-[420px] md:h-[320px] md:max-h-none"}`}
      data-testid="widget-unresolved"
    >
      <header className="flex items-center justify-between gap-2 mb-3 shrink-0 pr-6">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#F43F5E]/15 border border-[#F43F5E]/40 text-[#F43F5E] shrink-0">
            <ShieldAlert className="w-3.5 h-3.5" />
          </div>
          <h3 className="font-display text-sm font-bold tracking-tight truncate">
            Unresolved data
          </h3>
          <span
            className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded-full border shrink-0"
            style={{
              borderColor: total > 0 ? "rgba(244,63,94,0.4)" : "rgba(16,185,129,0.4)",
              color: total > 0 ? "#F43F5E" : "#10B981",
              background: total > 0 ? "rgba(244,63,94,0.05)" : "rgba(16,185,129,0.05)",
            }}
          >
            {total}
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[10px] uppercase tracking-wider hover:underline shrink-0 inline-flex items-center gap-0.5 text-[#F43F5E]"
          data-testid="widget-unresolved-refresh"
        >
          Refresh
        </button>
      </header>

      {/* Completeness bar */}
      <div className="shrink-0 mb-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#647087] mb-1">
          <span className="flex items-center gap-1">
            <Sparkles
              className="w-3 h-3"
              style={{ color: completeness_pct >= 95 ? "#10B981" : "#F59E0B" }}
            />
            Data completeness
          </span>
          <span
            className="font-mono font-bold"
            style={{
              color:
                completeness_pct >= 95
                  ? "#10B981"
                  : completeness_pct >= 70
                    ? "#F59E0B"
                    : "#F43F5E",
            }}
            data-testid="widget-unresolved-pct"
          >
            {completeness_pct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${completeness_pct}%`,
              background:
                completeness_pct >= 95
                  ? "linear-gradient(90deg, #10B981, #34D399)"
                  : completeness_pct >= 70
                    ? "linear-gradient(90deg, #F59E0B, #FBBF24)"
                    : "linear-gradient(90deg, #F43F5E, #FB7185)",
            }}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 hv-scroll">
        {err && (
          <div className="text-xs text-rose-300 italic py-2">{err}</div>
        )}
        {!err && sections.length === 0 && (
          <div className="text-xs text-[#647087] italic py-4 text-center">
            All data looks clean. Nice work.
          </div>
        )}
        {sections.map((s) => {
          const Icon = KIND_ICON[s.kind] || ShieldAlert;
          const isOpen = openSection === s.key;
          return (
            <div
              key={s.key}
              className="mb-2"
              data-testid={`widget-unresolved-section-${s.key}`}
            >
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? null : s.key)}
                className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded border border-white/10 hover:border-[#F43F5E]/40 transition"
              >
                <Icon className="w-3.5 h-3.5 text-[#F43F5E] shrink-0" />
                <span className="text-xs font-medium flex-1 min-w-0 truncate">
                  {s.title}
                </span>
                <span className="text-[10px] font-mono text-[#F43F5E] shrink-0">
                  {s.items.length}
                </span>
                <ChevronRight
                  className={`w-3 h-3 text-[#647087] shrink-0 transition-transform ${
                    isOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <div className="mt-1 ml-5 space-y-1">
                  <div className="text-[10px] text-[#647087] italic px-1">
                    {s.fix_hint}
                  </div>
                  <ul className="space-y-1">
                    {s.items.slice(0, 8).map((item) => (
                      <li
                        key={item.id}
                        className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/5 text-[11px]"
                      >
                        <span className="truncate min-w-0">
                          {item.name ||
                            item.title ||
                            item.short_id ||
                            item.external_ref ||
                            item.id}
                          {item.external_ref && item.name && (
                            <span className="text-[10px] font-mono text-[#647087] ml-1">
                              {item.external_ref}
                            </span>
                          )}
                        </span>
                        <Link
                          to={KIND_LINK(s.kind, item)}
                          className="text-[#1FB6C1] hover:underline shrink-0 inline-flex items-center gap-0.5"
                          data-testid={`widget-unresolved-link-${s.kind}-${item.id}`}
                        >
                          Fix <ArrowUpRight className="w-2.5 h-2.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {s.items.length > 8 && (
                    <div className="text-[10px] text-[#647087] italic px-1">
                      …and {s.items.length - 8} more.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
